import {
    AccountType, FormStatus, PaymentMode, PlanningFileType, ProjectFileSection, ProjectFormType,
    ProjectStep
} from 'src/utils/enums';
import { PROJECT_TYPE_LABELS } from '@autoriz/shared/project'
import { PresetFilterTypes } from 'types';

import type { TableFilters, TableSorting } from 'src/types/table'

export const AccountTypeName: Record<AccountType, string> = {
  ARCHITECT: 'Architecte',
  CLIENT: 'Client',
  COLLABORATOR: 'Collaborateur',
  SALESMAN: 'Commercial',
  SOCIETY: 'Société',
}

// Canonical labels now live in @autoriz/shared/project (PROJECT_TYPE_REGISTRY).
export const ProjectTypes = PROJECT_TYPE_LABELS

export const FormStatusName: Record<FormStatus, string> = {
  EMPTY: 'Vide',
  EDITED: 'Modifié',
  COMPLETED: 'Complet',
  VALIDATED: 'Accepté',
  REFUSED: 'Refusé',
  // Only an urban study reaches this (#695) — feminine because that is the
  // only noun it ever qualifies on screen ("l'étude est annulée").
  CANCELLED: 'Annulée',
}

export const ProjectFileSectionName: Record<ProjectFileSection, string> = {
  ADMINISTRATIVE: 'Aministratif',
  TOWN_PLANNING: 'Urbanisme',
  DESIGN: 'Dessins',
  PICTURES: 'Photos',
  OTHER_DOCUMENTS: 'Autres documents',
}

export const ProjectFormName: Record<ProjectFormType, string> = {
  DECLARER: 'déclarants',
  LAND: 'terrain',
  EXISTING_BUILDING: 'hâbitatiton',
  VERANDA: 'véranda',
  PERGOLA: 'pergola',
  POOL: 'piscine',
}

export enum CommunicationSupports {
  EMAIL = 'Mail',
  PHONE = 'Téléphone',
  NOTE = 'Note',
}

export const PlanningFileTypeName: Record<PlanningFileType, string> = {
  DP: 'Déclaration préalable',
  DP_ABF: 'Déclaration préalable avec ABF',
  PC: 'Permis de construire',
  PC_ABF: 'Permis de construire ABF',
  PC_SIGNED: "Permis de construire avec signature de l'architect",
  PC_SIGNED_ABF: "Permis de construire avec signature de l'architect et abf",
  PCM: 'Permis modificatif',
  PCM_ABF: 'Permis modificatif avec ABF',
  PCM_SIGNED: "Permis modificatif avec signature de l'architect",
  PCM_SIGNED_ABF: "Permis modificatif avec signature de l'architect et ABF",
}

export const PlanningFileTypeShortName: Record<PlanningFileType, string> = {
  DP: 'DP',
  DP_ABF: 'DP avec ABF',
  PC: 'PC',
  PC_ABF: 'PC avec ABF',
  PC_SIGNED: 'PC avec signature',
  PC_SIGNED_ABF: 'PC avec signature et ABF',
  PCM: 'PM',
  PCM_ABF: 'PM avec ABF',
  PCM_SIGNED: 'PM avec signature',
  PCM_SIGNED_ABF: 'PM avec signature et ABF',
}

export const PlanningFileTypeColor: Record<PlanningFileType, string> = {
  DP: '#d9f99d',
  DP_ABF: '#d9f99d',
  PC: '#fdba74',
  PC_ABF: '#fdba74',
  PC_SIGNED: '#fca5a5',
  PC_SIGNED_ABF: '#fca5a5',
  PCM: '#38bdf8',
  PCM_ABF: '#38bdf8',
  PCM_SIGNED: '#818cf8',
  PCM_SIGNED_ABF: '#818cf8',
}

export const PaymentModeName: Record<PaymentMode, string> = {
  IMMEDIATE: 'Paiement à la commande',
  END_OF_MONTH: "En fin de mois aprés l'envoie du dossier",
}

export const ProductKeyName: Record<string, string> = {
  PC: 'Permis de construire',
  DP: 'Déclaration préalable',
  ABF: 'Architecte des Bâtiments de France',
  SIGNING: 'Signature',
  SIGNING_5_19: 'Signature 5-19',
  SIGNING_19_39: 'Signature 19-39',
  SIGNING_39_60: 'Signature 39-60',
  URBAN_ANALYSIS: 'Étude urbaine',
}

export const PROJECT_PRESET_SORTERS: Record<PresetFilterTypes, TableSorting> = {
  all: [
    {
      field: 'created',
      order: 'desc',
    },
  ],
  creating: [
    {
      field: 'created',
      order: 'desc',
    },
  ],
  'to-control': [
    {
      field: 'created',
      order: 'desc',
    },
  ],
  'waiting-payement': [
    {
      field: 'created',
      order: 'desc',
    },
  ],
  'to-process': [
    {
      field: 'paid',
      order: 'desc',
    },
  ],
  completed: [],
  deposited: [
    {
      field: 'instruction_end',
      order: 'asc',
    },
  ],
  sended: [
    {
      field: 'first_sending_date',
      order: 'asc',
    },
  ],
}

export const PROJECT_PRESET_FILTERS: Record<PresetFilterTypes, TableFilters> = {
  all: [],
  creating: [
    {
      field: 'step',
      operator: 'in',
      value: [ProjectStep.PROJECT_INITIALIZED, ProjectStep.PROJECT_REFUSED],
    },
  ],
  'to-control': [
    {
      field: 'step',
      operator: 'in',
      value: [ProjectStep.PROJECT_REQUESTED],
    },
  ],
  'waiting-payement': [
    {
      field: 'step',
      operator: 'in',
      value: [ProjectStep.PROJECT_ACCEPTED, ProjectStep.PROJECT_ORDERED],
    },
  ],
  'to-process': [
    {
      field: 'step',
      operator: 'in',
      value: [
        ProjectStep.PROJECT_PAIED,
        ProjectStep.PROJECT_IN_PROGRESS,
        ProjectStep.PROJECT_FILE_COMPLETED,
        ProjectStep.PROJECT_FILE_REFUSED,
      ],
    },
  ],
  sended: [
    {
      field: 'step',
      operator: 'in',
      value: [ProjectStep.PROJECT_FILE_SENT],
    },
  ],
  deposited: [
    {
      field: 'step',
      operator: 'in',
      value: [ProjectStep.PROJECT_FILE_SUBMITTED],
    },
  ],
  completed: [
    {
      field: 'step',
      operator: 'in',
      value: [ProjectStep.PROJECT_COMPLETED, ProjectStep.PROJECT_ABANDONED],
    },
  ],
}

export const COOKIE_MAX_AGE = 30 * 24 * 60 * 60

export const ProjectStepName: Record<ProjectStep, string> = {
  PROJECT_INITIALIZED: 'Création en cours',
  PROJECT_IN_PROGRESS: 'Rédaction des documents en cours',
  PROJECT_ACCEPTED: 'Commande en attente',
  PROJECT_REFUSED: 'Refusé',
  PROJECT_REQUESTED: 'En attente de vérification',
  PROJECT_PAIED: 'Payé',
  PROJECT_ORDERED: 'Paiement en attente',
  PROJECT_FILE_COMPLETED: 'Rédaction des documents terminée',
  PROJECT_FILE_SENT: 'Documents envoyés',
  PROJECT_FILE_SUBMITTED: 'Déposé',
  PROJECT_FILE_REFUSED: 'Refus administratif',
  PROJECT_ABANDONED: 'Abandonné',
  PROJECT_COMPLETED: 'Terminé',
}

export const PROJECT_TABLE_SIZE = 20;
